import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, Circle, Clock, PauseCircle, XCircle, Archive, FlaskConical, Info, CloudOff, Bookmark } from "lucide-react";

export const cx = (...a) => a.filter(Boolean).join(" ");

export function Btn({ variant = "primary", className = "", children, testid, ...p }) {
  const v = {
    primary: "bg-primary text-primary-foreground hover:bg-clay-strong",
    soft: "bg-peach text-clay-strong hover:bg-peach-hover",
    sage: "bg-secondary text-secondary-foreground hover:bg-sage-hover",
    ghost: "bg-transparent text-foreground hover:bg-muted",
    outline: "bg-card border border-input text-foreground hover:border-ink hover:bg-sand",
    danger: "bg-white border border-rose-line text-destructive hover:bg-rose",
  }[variant];
  return (
    <button data-testid={testid} className={cx("inline-flex min-h-control items-center justify-center gap-2 rounded-xl px-4 py-2 text-[15px] font-semibold leading-tight transition-colors disabled:opacity-50 disabled:cursor-not-allowed", v, className)} {...p}>
      {children}
    </button>
  );
}

export function Card({ className = "", children, ...p }) {
  return <div className={cx("rounded-2xl border bg-card p-5 sm:p-6 shadow-card", className)} {...p}>{children}</div>;
}

export function Field({ label, error, hint, children, htmlFor, required }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={htmlFor} className="block text-sm font-semibold text-foreground">
          {label} {required && <span className="text-muted-foreground font-normal">({t("common.required")})</span>}
        </label>
      )}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && (
        <p role="alert" data-testid={`field-error-${htmlFor || "x"}`} className="flex items-start gap-1 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </p>
      )}
    </div>
  );
}

const inputCls = "w-full min-h-control rounded-xl border border-input bg-card px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground/80 focus:outline-none focus:ring-2 focus:ring-ring";
export const Input = ({ className = "", ...p }) => <input className={cx(inputCls, className)} {...p} />;
export const Textarea = ({ className = "", ...p }) => <textarea rows={3} className={cx(inputCls, "min-h-[88px]", className)} {...p} />;
export const Select = ({ className = "", children, ...p }) => <select className={cx(inputCls, "pr-8", className)} {...p}>{children}</select>;

const STATUS_STYLE = {
  ok: ["bg-sage text-sage-ink border-sage-line", CheckCircle2],
  progress: ["bg-lavender text-lavender-ink border-lavender-line", Clock],
  warn: ["bg-amber-soft text-amber-ink border-amber-line", AlertCircle],
  stop: ["bg-rose text-rose-ink border-rose-line", XCircle],
  neutral: ["bg-muted text-neutral-ink border-sand-line", Circle],
  pause: ["bg-peach text-clay-strong border-peach-line", PauseCircle],
  archive: ["bg-muted text-neutral-ink border-sand-line", Archive],
};
const TONE = {
  active: "ok", completed: "ok", performed: "ok", sent: "ok", accepted: "ok", succeeded: "ok", reviewed: "ok", recorded: "ok", done: "ok",
  draft: "progress", scheduled: "progress", started: "progress", draft_outcome: "warn", pending: "progress", open: "warn",
  performed_with_changes: "warn", partly_succeeded: "warn", help_needed: "warn", replaced: "archive", deferred: "pause",
  stopped: "stop", discontinued: "neutral", cancelled: "neutral", did_not_take_place: "neutral", erroneous: "neutral",
  withdrawn: "neutral", revoked: "stop", expired: "neutral", not_attempted: "neutral", no_response: "neutral",
  did_not_succeed: "neutral", not_performed: "neutral", superseded: "archive",
};

export function StatusChip({ status, label, testid }) {
  const { t } = useTranslation();
  const [cls, Icon] = STATUS_STYLE[TONE[status] || "neutral"];
  return (
    <span data-testid={testid || `status-${status}`} className={cx("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold", cls)}>
      <Icon className="h-3.5 w-3.5" aria-hidden /> {label || t(`status.${status}`)}
    </span>
  );
}

export function SampleBadge({ testid = "sample-badge" }) {
  const { t } = useTranslation();
  return <span data-testid={testid} className="inline-flex items-center gap-1 rounded-md border border-sky-line bg-sky px-2 py-0.5 text-xs font-bold text-sky-ink"><Bookmark className="h-3.5 w-3.5" aria-hidden />{t("b4.sample")}</span>;
}

export function DemoBadge({ testid = "demo-badge", short }) {
  const { t } = useTranslation();
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 rounded-md border border-amber-line bg-amber px-2 py-0.5 text-xs font-bold text-amber-ink">
      <FlaskConical className="h-3.5 w-3.5" aria-hidden /> {short ? t("demo.short") : t("demo.badge")}
    </span>
  );
}

export function Tag({ children, className = "", ...p }) {
  return <span className={cx("inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-neutral-ink", className)} {...p}>{children}</span>;
}

export function SourceTag({ source }) {
  const { t } = useTranslation();
  if (!source) return null;
  return <Tag data-testid={`source-${source}`} className="bg-lavender text-lavender-ink">{t(`source.${source}`)}</Tag>;
}

export function ErrorSummary({ errors, title, testid = "error-summary" }) {
  const { t } = useTranslation();
  if (!errors?.length) return null;
  return (
    <div role="alert" data-testid={testid} className="rounded-2xl border border-rose-line bg-rose p-4 text-sm">
      <p className="mb-2 flex items-center gap-2 font-semibold text-rose-ink"><AlertCircle className="h-4 w-4" /> {title || t("err.summary_title")}</p>
      <ul className="list-disc space-y-1 pl-6 text-rose-ink">
        {errors.map((e, i) => <li key={i}>{e.text || `${t(`field.${e.field}`, { defaultValue: e.field })}: ${t(`err.${e.code}`)}`}</li>)}
      </ul>
    </div>
  );
}

export function Notice({ children, tone = "info", testid, icon: IconProp }) {
  const cls = { info: "bg-lavender border-lavender-line text-lavender-ink", warn: "bg-amber-soft border-amber-line text-amber-ink",
    ok: "bg-sage border-sage-line text-sage-ink", offline: "bg-peach border-peach-line text-clay-strong" }[tone];
  const I = IconProp || (tone === "offline" ? CloudOff : Info);
  return <div data-testid={testid} role="status" className={cx("flex items-start gap-2 rounded-2xl border p-3.5 text-sm", cls)}><I className="mt-0.5 h-4 w-4 shrink-0" />{<div className="flex-1">{children}</div>}</div>;
}

export function Empty({ title, action, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-dashed bg-white/60 p-8 text-center">
      <img src="/img/b9/empty_state.webp" alt="" aria-hidden loading="lazy" className="mx-auto mb-4 h-28 w-auto rounded-xl object-contain" />
      <p className="mb-4 text-muted-foreground">{title}</p>
      {action}
    </div>
  );
}

export function PageTitle({ title, sub, right, badge }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between fade-up">
      <div className="min-w-0">
        <h1 className="font-display text-3xl font-semibold text-ink sm:text-4xl break-words">{title}</h1>
        {(sub || badge) && <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">{badge}{sub}</div>}
      </div>
      {right && <div className="flex flex-wrap gap-2">{right}</div>}
    </div>
  );
}

export function Meta({ label, children, testid }) {
  return (
    <div data-testid={testid} className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-[15px]">{children || "—"}</dd>
    </div>
  );
}

export function useFieldErrors(apiErr) {
  const { t } = useTranslation();
  const list = apiErr?.errors || [];
  const errFor = (f) => {
    const e = list.find((x) => x.field === f);
    return e ? t(`err.${e.code}`) : null;
  };
  return { list, errFor };
}
